


import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Typography, Button, IconButton,
  InputAdornment, useMediaQuery, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../Commonlibs/userSlice';
import Loader from '../Loader/Loader';
// Import assets
import leftImage from '../assets/logip.png';
import houseLogo from '../assets/lob.png';
import lockIcon from '../assets/lock.png';
import emailIcon from '../assets/email.png';

const ForgotPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const userFromStore = useSelector(selectUser);
  
  // Form state
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  
  // Dialog state
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null
  });
  
  // Initialize email from location state or store if available
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    } else if (userFromStore && userFromStore.email) {
      setEmail(userFromStore.email);
    }
  }, [userFromStore, location.state]);
  
  const handleCloseDialog = () => {
    setDialog({...dialog, open: false});
  };
  
  const showDialog = (title, message, action) => {
    setDialog({
      open: true,
      title,
      message,
      action
    });
  };
  
  // Step 1: Request OTP
  const handleRequestOTP = async () => {
    if (!email) {
      showDialog('Error', 'Please enter your email address.', null);
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showDialog('Error', 'Please enter a valid email address.', null);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordRequest-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_id: email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        setUserId(data.result.user_id);
        showDialog(
          'OTP Sent', 
          'OTP has been sent to your email!', 
          () => {
            handleCloseDialog();
            setCurrentStep(2);
          }
        );
      } else {
        showDialog('Error', data.message || 'Failed to send OTP. Try again.', null);
      }
    } catch (error) {
      console.error('OTP request error:', error);
      showDialog('Error', 'Something went wrong. Please try again later.', null);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp) {
      showDialog('Error', 'Please enter the OTP.', null);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordVerify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: userId,
          otp: otp 
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        setTempToken(data.result?.temp_token);
        showDialog(
          'OTP Verified', 
          'OTP verified successfully!', 
          () => {
            handleCloseDialog();
            setCurrentStep(3);
          }
        );
      } else {
        showDialog('Error', data.message || 'Invalid or expired OTP.', null);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      showDialog('Error', 'Something went wrong. Please try again later.', null);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      showDialog('Error', 'Please fill in all password fields.', null);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showDialog('Error', 'Passwords do not match.', null);
      return;
    }
    
    if (newPassword.length < 6) {
      showDialog('Error', 'Password must be at least 6 characters.', null);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('https://propertydealstalk.com/api/users/passwordReset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          new_password: newPassword,
          temp_token: tempToken
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === "SUCCESS") {
        showDialog(
          'Success', 
          'Password was reset successfully!', 
          () => {
            handleCloseDialog();
            navigate('/login');
          }
        );
      } else {
        showDialog('Error', data.message || 'Failed to reset password. Try again.', null);
      }
    } catch (error) {
      console.error('Password reset error:', error);
      showDialog('Error', 'Something went wrong. Please try again later.', null);
    } finally {
      setLoading(false);
    }
  };
  
  // Render form based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Enter your email to receive OTP</Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter your email address*"
              margin="dense"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleRequestOTP}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Get OTP
            </Button>
          </>
        );
      
      case 2:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Verify your identity</Typography>
            <TextField
              fullWidth
              disabled
              value={email}
              placeholder="Your email address"
              margin="dense"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontSize="13px" mb={0.5}>Enter OTP</Typography>
            <Typography fontSize="11px" mb={0.5} color="gray">
              (Please check your email for the verification code)
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyOTP}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Verify OTP
            </Button>
          </>
        );
      
      case 3:
        return (
          <>
            <Typography fontSize="16px" mb={1} color="#000000">Create New Password</Typography>
            <TextField
              fullWidth
              disabled
              value={email}
              placeholder="Your email address"
              margin="dense"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={emailIcon} alt="email" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter new password*"
              margin="dense"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={lockIcon} alt="lock" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password*"
              margin="dense"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={lockIcon} alt="lock" style={{ width: 18, marginRight: 8 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleResetPassword}
              sx={{
                backgroundColor: '#0052ff',
                color: '#fff',
                py: 1.2,
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Change Password
            </Button>
          </>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} height="100vh" width="100%">
      {loading && <Loader />}
      
      {/* Success Dialog */}
      <Dialog
        open={dialog.open}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '8px',
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{ 
            textAlign: 'center',
            color: dialog.title === 'Success' || dialog.title === 'OTP Verified' || dialog.title === 'OTP Sent' ? '#4caf50' : undefined,
            fontWeight: 500,
            pt: 3
          }}
        >
          {dialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{ textAlign: 'center' }}
          >
            {dialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 1 }}>
          <Button 
            onClick={dialog.action || handleCloseDialog} 
            color="primary" 
            variant="contained"
            autoFocus
            sx={{
              bgcolor: '#0052ff',
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            {currentStep === 3 && dialog.title === 'Success' ? 'CLICK TO LOGIN' : 'CLICK TO CONTINUE'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Left Image Section */}
      <Box
        width={isMobile ? '100%' : '50%'}
        height={isMobile ? '30vh' : '100%'}
        sx={{ position: 'relative' }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 1
          }}
        />
        <img
          src={leftImage}
          alt="left"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
      {/* Right Form Section */}
      <Box
        width={isMobile ? '100%' : '60%'}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        px={2}
        py={isMobile ? 3 : 0}
        sx={{ backgroundColor: '#fff' }}
      >
        <img src={houseLogo} alt="house" style={{ width: 45, height: 45, marginBottom: 10 }} />
        <Typography variant="h6" fontWeight={500} mb={2}>
          {currentStep === 1 ? 'Forgot Password' : 
           currentStep === 2 ? 'Verify OTP' : 
           'Create New Password'}
        </Typography>
        <Box
          sx={{
            width: '100%',
            maxWidth: 350,
            borderRadius: 3,
            p: 2,
            border: '1px solid #eee',
            boxShadow: '0 0 20px rgba(0,0,0,0.05)'
          }}
        >
          {renderStepContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;